/**
 * Working Billing Claim Utilities
 *
 * Pure transform helpers for reading from and writing to the
 * WorkingBillingClaim structure on encounter responses.
 *
 * Naming is code-system-agnostic — `code_id` supports formats like
 * `CPT:99213`, `HCPCS:H2009`, etc.
 */

import type { WorkingClaimLineItem } from '@store/generated/encounters';

import type {
  WorkingBillingClaim,
  WorkingBillingClaimLineItem,
  WorkingBillingClaimPutBody,
} from '../types';

/**
 * Strip the code system prefix from a code_id.
 * e.g. `CPT:99213` → `99213`, `HCPCS:H2009` → `H2009`
 * If no prefix, returns the code as-is.
 */
export const stripCodeSystem = (codeId: string): string => {
  const colonIndex = codeId.indexOf(':');
  return colonIndex !== -1 ? codeId.slice(colonIndex + 1) : codeId;
};

/**
 * Extract bare billing code strings from a working billing claim's line items.
 * Strips the code system prefix (e.g. `CPT:99213` → `99213`).
 * Returns empty array if claim or line_items is null/undefined.
 */
export const extractBillingCodes = (
  claim: WorkingBillingClaim | null | undefined,
): string[] => {
  if (!claim?.line_items?.length) return [];
  return claim.line_items
    .map(li => (li.code_id ? stripCodeSystem(li.code_id) : ''))
    .filter(Boolean);
};

/**
 * Extract diagnosis codes from a working billing claim.
 * Returns empty array if claim or diagnosis_codes is null/undefined.
 */
export const extractDiagnosisCodes = (
  claim: WorkingBillingClaim | null | undefined,
): string[] => {
  return claim?.diagnosis_codes ?? [];
};

/**
 * Extract place of service from a working billing claim.
 * Defaults to '10' (telehealth - patient home) if not set.
 */
export const extractPlaceOfService = (
  claim: WorkingBillingClaim | null | undefined,
): string => {
  return claim?.place_of_service ?? '10';
};

/**
 * Check if a claim has any non-empty line items.
 */
export const hasLineItems = (
  claim: WorkingBillingClaim | null | undefined,
): boolean => {
  return (claim?.line_items?.length ?? 0) > 0;
};

/**
 * Check if a claim has any non-empty diagnosis codes.
 */
export const hasDiagnosisCodes = (
  claim: WorkingBillingClaim | null | undefined,
): boolean => {
  return (claim?.diagnosis_codes?.length ?? 0) > 0;
};

/**
 * Convert bare code strings to WorkingClaimLineItem[].
 *
 * Matches by bare code against existing items to preserve metadata
 * (modifiers, source). New codes get
 * `source: 'provider_selected'` and a default `CPT:` prefix.
 */
export const bareCodesToLineItems = (
  codes: string[],
  existingItems?: WorkingBillingClaimLineItem[],
): WorkingClaimLineItem[] => {
  return codes.map(bareCode => {
    // Try to find an existing line item that matches this bare code
    const existing = existingItems?.find(
      li => li.code_id && stripCodeSystem(li.code_id) === bareCode,
    );

    if (existing) {
      return {
        code_id: existing.code_id ?? `CPT:${bareCode}`,
        source: existing.source ?? 'provider_selected',
        modifiers: existing.modifiers,
        modifier_sources: existing.modifier_sources,
        units: existing.units ?? undefined,
        charge_cents: existing.charge_cents ?? undefined,
        diagnosis_codes: existing.diagnosis_codes,
      };
    }

    // New code — default to CPT prefix since current pickers emit CPT-shaped codes
    return {
      code_id: `CPT:${bareCode}`,
      source: 'provider_selected' as const,
    };
  });
};

type DiagnosisCodeSource = 'provider_selected' | 'ai_recommended';

/**
 * Build a diagnosis_code_sources map from codes.
 *
 * Preserves provenance for codes that already exist in `existingSources`.
 * New codes are marked with `defaultSource` (defaults to 'provider_selected').
 */
export const buildDiagnosisCodeSources = (
  codes: string[],
  existingSources?: Record<string, DiagnosisCodeSource> | null,
  defaultSource: DiagnosisCodeSource = 'provider_selected',
): Record<string, DiagnosisCodeSource> => {
  const sources: Record<string, DiagnosisCodeSource> = {};
  for (const code of codes) {
    sources[code] = existingSources?.[code] ?? defaultSource;
  }
  return sources;
};

/**
 * Build a complete PUT request body from the current claim + partial updates.
 * Includes `expected_updated_at` from the claim for optimistic concurrency.
 *
 * This is a full-replace operation: fields not provided in `updates`
 * are carried forward from the current claim.
 */
export const buildClaimPutRequest = (
  currentClaim: WorkingBillingClaim | null | undefined,
  updates: {
    lineItems?: WorkingClaimLineItem[];
    diagnosisCodes?: string[];
    diagnosisCodeSources?: Record<string, DiagnosisCodeSource>;
    placeOfService?: string | null;
  },
): WorkingBillingClaimPutBody => {
  const request: WorkingBillingClaimPutBody = {
    expected_updated_at: currentClaim?.updated_at ?? null,
  };

  // Line items: use update if provided, otherwise carry forward existing
  if (updates.lineItems !== undefined) {
    request.line_items = updates.lineItems;
  } else if (currentClaim?.line_items) {
    request.line_items = currentClaim.line_items.map(li => ({
      code_id: li.code_id ?? '',
      source: li.source ?? 'provider_selected',
      modifiers: li.modifiers,
      modifier_sources: li.modifier_sources,
      units: li.units ?? undefined,
      charge_cents: li.charge_cents ?? undefined,
      diagnosis_codes: li.diagnosis_codes,
    }));
  }

  // Diagnosis codes: use update if provided, otherwise carry forward
  if (updates.diagnosisCodes !== undefined) {
    request.diagnosis_codes = updates.diagnosisCodes;
    if (updates.diagnosisCodeSources !== undefined) {
      request.diagnosis_code_sources = updates.diagnosisCodeSources;
    }
  } else if (currentClaim?.diagnosis_codes) {
    request.diagnosis_codes = currentClaim.diagnosis_codes;
    if (currentClaim.diagnosis_code_sources) {
      request.diagnosis_code_sources = currentClaim.diagnosis_code_sources;
    }
  }

  // Place of service: use update if provided, otherwise carry forward
  if (updates.placeOfService !== undefined) {
    request.place_of_service = updates.placeOfService;
  } else if (currentClaim?.place_of_service) {
    request.place_of_service = currentClaim.place_of_service;
  }

  return request;
};
